<template>
    <div
        v-if="message.type === 'tool_status'"
        class="my-1 text-left text-xs text-gray-400"
    >
        {{ t("vue.ai_chat_tool_loaded") }}
        <span class="font-medium">{{ message.tool_name }}</span>
    </div>

    <div
        v-else
        class="flex items-start"
        :class="message.type === 'user' ? 'justify-end' : 'justify-start'"
    >
        <div ref="contentRef" class="max-w-[80%]">
            <div
                v-if="originChip"
                class="mb-1 flex flex-wrap items-center gap-1"
                :class="
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                "
            >
                <component
                    :is="returnHref ? 'button' : 'span'"
                    :type="returnHref ? 'button' : undefined"
                    class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
                    :class="
                        returnHref
                            ? 'cursor-pointer transition hover:bg-[#FF421E]/10 hover:text-[#FF421E]'
                            : ''
                    "
                    :title="
                        returnHref
                            ? t('vue.ai_widget_return_to_page')
                            : originChip.title
                    "
                    @click="returnToSource"
                >
                    <component :is="originChip.icon" class="h-3 w-3 shrink-0" />
                    <span class="truncate">{{ originChip.label }}</span>
                    <ArrowUturnLeftIcon
                        v-if="returnHref"
                        class="h-3 w-3 shrink-0"
                    />
                </component>
            </div>

            <div
                class="overflow-hidden rounded-2xl px-4 py-2 text-sm"
                :class="
                    message.type === 'user'
                        ? 'whitespace-pre-wrap break-words bg-gray-800 text-white'
                        : 'border border-gray-200 bg-white text-gray-800'
                "
            >
                <AiChatMarkdown
                    v-if="message.type === 'assistant'"
                    :content="message.content ?? ''"
                />
                <template v-else>
                    <AiChatMessageImage
                        v-if="message.attachment"
                        :attachment="message.attachment"
                        :dark="true"
                    />
                    <template v-if="message.content">{{
                        message.content
                    }}</template>
                </template>
            </div>

            <div
                v-if="message.type === 'user' && message.skill"
                class="mt-1 flex flex-wrap items-center gap-1 justify-end"
            >
                <span
                    class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-[#FF421E]/10 px-2 py-0.5 text-[10px] font-medium text-[#FF421E]"
                    :title="t('vue.ai_skill_attached')"
                >
                    <SparklesIcon class="h-3 w-3 shrink-0" />
                    <span class="truncate">{{ message.skill.name }}</span>
                </span>
            </div>

            <div
                class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400"
                :class="
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                "
            >
                <span class="font-medium">{{ roleLabel }}</span>
                <span v-if="timeLabel">· {{ timeLabel }}</span>
                <span v-if="durationLabel">· {{ durationLabel }}</span>
                <span v-if="tokensLabel">· {{ tokensLabel }}</span>
                <span v-if="modelLabel">· {{ modelLabel }}</span>
            </div>
        </div>

        <AiChatMessageActions
            v-if="message.type === 'assistant' && chatId !== null"
            class="mt-1 ml-1.5 shrink-0"
            :chat-id="chatId"
            :message-id="message.id"
            :default-title="defaultTitle"
            :compact="compactActions"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { router } from "@inertiajs/vue3";
import {
    MapPinIcon,
    ChatBubbleLeftRightIcon,
    Squares2X2Icon,
    ArrowUturnLeftIcon,
    SparklesIcon,
} from "@heroicons/vue/24/outline";
import type { IAiChatMessage } from "@/types/AiChat/IAiChat";
import { useTranslations } from "@/composables/useTranslations";

const props = withDefaults(
    defineProps<{
        message: IAiChatMessage;
        chatId?: number | null;
    }>(),
    { chatId: null },
);

const { t } = useTranslations();

// The three action icons collapse into a single popup menu when they would
// crowd the reply: either the message is too narrow, or it is shorter than the
// stacked toolset itself (which would then stick out below a short message).
const COMPACT_ACTIONS_WIDTH = 300;
const COMPACT_ACTIONS_HEIGHT = 80;
const contentRef = ref<HTMLElement | null>(null);
const compactActions = ref(false);
let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
    if (contentRef.value === null || typeof ResizeObserver === "undefined") {
        return;
    }
    resizeObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect;
        compactActions.value =
            width < COMPACT_ACTIONS_WIDTH || height < COMPACT_ACTIONS_HEIGHT;
    });
    resizeObserver.observe(contentRef.value);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});

const defaultTitle = computed(() => {
    const raw = props.message.content ?? "";
    const withoutBlocks = raw.replace(
        /```(dmarceye-chart|dmarceye-stats)[\s\S]*?```/g,
        "",
    );
    for (const line of withoutBlocks.split("\n")) {
        const cleaned = line
            .replace(/^[#*\-\s>]+/, "")
            .replace(/[*`]/g, "")
            .trim();
        if (cleaned !== "") {
            return cleaned.slice(0, 120);
        }
    }
    return t("vue.ai_chat_role_assistant");
});

const roleLabel = computed(() =>
    props.message.type === "user"
        ? t("vue.ai_chat_role_you")
        : t("vue.ai_chat_role_assistant"),
);

const timeLabel = computed(() => {
    if (!props.message.created_at) {
        return "";
    }
    const date = new Date(props.message.created_at);
    if (isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
});

const durationLabel = computed(() => {
    const ms = props.message.meta?.duration_ms;
    if (typeof ms !== "number") {
        return "";
    }
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
});

const tokensLabel = computed(() => {
    const tokens = props.message.meta?.tokens;
    if (typeof tokens !== "number") {
        return "";
    }
    return `${new Intl.NumberFormat().format(tokens)} ${t("vue.ai_chat_tokens")}`;
});

const modelLabel = computed(() => props.message.meta?.model ?? "");

// A widget-originated message can send the user back to the exact page they
// asked from, re-opening the mini chat on that page with this conversation
// active (via the ai_widget_chat param the widget reads on arrival).
const returnHref = computed<string | null>(() => {
    const origin = props.message.origin;
    if (!origin || origin.source !== "widget" || !origin.url) {
        return null;
    }
    const target = new URL(origin.url, window.location.origin);
    if (props.chatId !== null) {
        target.searchParams.set("ai_widget_chat", String(props.chatId));
    }
    return target.pathname + target.search + target.hash;
});

const returnToSource = (): void => {
    if (returnHref.value !== null) {
        router.visit(returnHref.value);
    }
};

// Shows where a user message was asked from: the mini chat (optionally with the
// page it was scoped to) or the full-page chat. A widget message always states
// "Mini chat" so its origin is explicit, plus the page/URL when present.
const originChip = computed(() => {
    const origin = props.message.origin;
    if (!origin || props.message.type !== "user") {
        return null;
    }

    if (origin.source === "widget") {
        const urlPath = origin.url ? origin.url.split("?")[0] : null;
        const page = origin.page ?? urlPath;
        const mini = t("vue.ai_widget_origin_mini");
        return {
            icon: page ? MapPinIcon : ChatBubbleLeftRightIcon,
            label: page ? `${mini} · ${page}` : mini,
            title: page
                ? `${t("vue.ai_widget_origin_page")}${urlPath ? ` · ${urlPath}` : ""}`
                : mini,
        };
    }

    return {
        icon: Squares2X2Icon,
        label: t("vue.ai_widget_origin_full"),
        title: t("vue.ai_widget_origin_full"),
    };
});
</script>
